import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Link, browserHistory } from 'react-router'
import { Card } from 'components'
import { Spinner } from 'lp-components'
import { LEARNING_HUB_ROUTE } from 'config'
import * as apiActions from 'api-actions'
import { selectors as globalSelectors } from '../global-reducer'
import {
  isBlockCompleted,
  isBlockUnlockedInCourse,
  isChapterCompleted,
} from '../utils/block-navigation'
import { courseForumPath } from '../utils/course-forum-url'
import { chapterBlockPath } from '../utils/course-paths'
import { getPreviewTokenForCourse } from '../utils/preview-mode'
import zoomIcon from 'images/zoom.svg'
import LearningHubBlockTypeIcon from './learning-hub-block-type-icon'

const propTypes = {
  params: PropTypes.shape({
    courseId: PropTypes.string.isRequired,
  }).isRequired,
  currentCourse: PropTypes.object,
  fetchLearningHubCourse: PropTypes.func.isRequired,
  fetchLearningHubCoursePreview: PropTypes.func.isRequired,
  // Set to true when mounted under /learning-hub/course-preview. Forces every
  // block to be navigable (no completion-based unlock gating), swaps the
  // fetch to the token-validated preview endpoint, and hides the learner-
  // only forum + catalog breadcrumb chrome.
  isPreviewMode: PropTypes.bool,
}

const defaultProps = {
  currentCourse: undefined,
  isPreviewMode: false,
}

function blockTypeLabel(type) {
  if (!type) return 'Block'
  if (type === 'scorm_file') return 'SCORM'
  return type.charAt(0).toUpperCase() + type.slice(1)
}

function ChapterOutlineIcon({ block }) {
  if (isBlockCompleted(block)) {
    return (
      <span
        className="learning-hub-chapter-overview__block-icon learning-hub-chapter-overview__block-icon--completed"
        aria-hidden
      >
        <svg
          width={22}
          height={22}
          viewBox="0 0 22 22"
          xmlns="http://www.w3.org/2000/svg"
          className="learning-hub-chapter-overview__block-complete-svg"
        >
          <circle cx="11" cy="11" r="11" fill="#087B2E" />
          <path
            d="M6 11.4 L9.6 15 16 8.3"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    )
  }
  if (block?.blockType === 'zoom') {
    return (
      <span
        className="learning-hub-chapter-overview__block-icon learning-hub-chapter-overview__block-icon--zoom"
        aria-hidden
      >
        <img
          src={zoomIcon}
          alt=""
          width={22}
          height={22}
          className="learning-hub-chapter-overview__block-icon-zoom-img"
        />
      </span>
    )
  }
  return (
    <span className="learning-hub-chapter-overview__block-icon" aria-hidden>
      <LearningHubBlockTypeIcon
        blockType={block?.blockType}
        className="learning-hub-chapter-overview__block-type-svg"
      />
    </span>
  )
}

ChapterOutlineIcon.propTypes = {
  block: PropTypes.object.isRequired,
}

function ChapterCompleteIcon() {
  return (
    <span
      className="learning-hub-chapter-overview__chapter-complete-icon"
      aria-hidden
    >
      <svg
        width={20}
        height={20}
        viewBox="0 0 22 22"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="11" cy="11" r="11" fill="#087B2E" />
        <path
          d="M6 11.4 L9.6 15 16 8.3"
          fill="none"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

function ChapterOverviewPage({
  params,
  currentCourse,
  fetchLearningHubCourse,
  fetchLearningHubCoursePreview,
  isPreviewMode,
}) {
  const courseId = Number(params.courseId)
  const [showChapters, setShowChapters] = useState(true)
  const [selectedChapterId, setSelectedChapterId] = useState(null)

  // Preview-mode fetches use the per-course JWT stashed in localStorage by
  // CoursePreviewShell. If the admin reloaded /course-preview/course/:id
  // directly without coming through the entry shell (e.g. opened a bookmark
  // from a previous session), the token may be missing; the preview API
  // call will then 401 and the page renders an empty state.
  const previewToken = isPreviewMode ? getPreviewTokenForCourse(courseId) : null

  useEffect(() => {
    if (Number.isNaN(courseId)) return
    if (isPreviewMode) {
      if (!previewToken) return
      fetchLearningHubCoursePreview(courseId, previewToken)
      return
    }
    fetchLearningHubCourse(courseId)
  }, [
    courseId,
    isPreviewMode,
    previewToken,
    fetchLearningHubCourse,
    fetchLearningHubCoursePreview,
  ])

  const course =
    currentCourse && Number(currentCourse.id) === courseId
      ? currentCourse
      : undefined

  const chapters = useMemo(() => course?.chapters || [], [course])

  useEffect(() => {
    if (!chapters.length) return
    if (selectedChapterId === null) {
      setSelectedChapterId(chapters[0].id)
      return
    }
    const exists = chapters.some((chapter) => chapter.id === selectedChapterId)
    if (!exists) setSelectedChapterId(chapters[0].id)
  }, [chapters, selectedChapterId])

  const selectedChapter =
    chapters.find((chapter) => chapter.id === selectedChapterId) || chapters[0]
  const blocks = selectedChapter?.blocks || []

  if (!course) {
    return (
      <div className="content-block-container learning-hub-chapter-overview">
        <Spinner />
      </div>
    )
  }

  // In preview mode every block is openable regardless of attempt state:
  // an admin reviewing the course outline shouldn't be blocked by their own
  // (non-existent) attempt history. The `isBlockUnlockedInCourse` util is
  // still applied for learners where the gating prevents skipping ahead.
  const isBlockUnlocked = (block) =>
    isPreviewMode || isBlockUnlockedInCourse(course, block.id)

  return (
    <div className="content-block-container learning-hub-chapter-overview">
      {isPreviewMode && (
        <div className="learning-hub-preview-banner" role="status">
          <p>You are viewing a Course Preview. Block progress is not saved.</p>
        </div>
      )}
      {!isPreviewMode && (
        <nav
          className="learning-hub-chapter-overview__breadcrumb"
          aria-label="Breadcrumb"
        >
          <Link to={`${LEARNING_HUB_ROUTE}/course-catalog`}>
            Course Catalog
          </Link>
          <span>/</span>
          <span>{course.title}</span>
          <span>/</span>
          <span>Chapters</span>
        </nav>
      )}

      <header className="learning-hub-chapter-overview__header">
        <h1>{course.title}</h1>
        <p>{course.instructor}</p>
      </header>

      <div className="learning-hub-chapter-overview__grid">
        <Card className="learning-hub-chapter-overview__sidebar">
          <h3 className="learning-hub-chapter-overview__course-title">
            {course.title}
          </h3>
          <button
            type="button"
            className="learning-hub-chapter-overview__chapter-toggle"
            aria-expanded={showChapters}
            onClick={() => setShowChapters((prev) => !prev)}
          >
            <span>Chapters</span>
            <span
              className="learning-hub-chapter-overview__chapter-chevron"
              aria-hidden
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={10}
                height={6}
                viewBox="0 0 10 6"
                fill="none"
              >
                <path
                  d="M5 6L0 1.13514L1.16667 0L5 3.72973L8.83333 0L10 1.13514L5 6Z"
                  fill="currentColor"
                />
              </svg>
            </span>
          </button>

          {showChapters && (
            <div className="learning-hub-chapter-overview__chapter-list">
              {chapters.map((chapter, idx) => {
                const chapterComplete =
                  !isPreviewMode && isChapterCompleted(chapter)
                return (
                  <button
                    key={chapter.id}
                    type="button"
                    className={`learning-hub-chapter-overview__chapter-item ${
                      selectedChapter?.id === chapter.id ? 'is-active' : ''
                    } ${chapterComplete ? 'is-completed' : ''}`}
                    onClick={() => setSelectedChapterId(chapter.id)}
                  >
                    {chapterComplete ? <ChapterCompleteIcon /> : null}
                    <span>{`Chapter ${idx + 1}`}</span>
                  </button>
                )
              })}
            </div>
          )}

          {!isPreviewMode && (
            <button
              type="button"
              className="learning-hub-chapter-overview__forum-button"
              onClick={() => browserHistory.push(courseForumPath(course))}
            >
              Go to Course Forum
            </button>
          )}
        </Card>

        <section className="learning-hub-chapter-overview__content">
          <h2>
            {`Chapter ${
              chapters.findIndex(
                (chapter) => chapter.id === selectedChapter?.id
              ) + 1
            }: ${selectedChapter?.title || 'Chapter'}`}
          </h2>
          <p className="learning-hub-chapter-overview__meta">
            {`${selectedChapter?.blockCount || 0} Blocks`}
            <span>·</span>
            {`${selectedChapter?.totalMinutes || 0} minutes`}
            <span>·</span>
            {`${selectedChapter?.formCount || 0} Form${
              Number(selectedChapter?.formCount || 0) === 1 ? '' : 's'
            }`}
          </p>

          <div className="learning-hub-chapter-overview__block-list">
            {blocks.map((block) => (
              <Card
                key={block.id}
                className={`learning-hub-chapter-overview__block-item ${
                  isBlockUnlocked(block) ? '' : 'is-locked'
                }`}
              >
                <div className="learning-hub-chapter-overview__block-left">
                  <ChapterOutlineIcon block={block} />
                  <div>
                    <h4>{block.title}</h4>
                    <p>{`${blockTypeLabel(block.blockType)} · ${
                      block.estimatedTime || 0
                    } min`}</p>
                  </div>
                </div>

                {isBlockUnlocked(block) ? (
                  <button
                    type="button"
                    className="button-primary button-small"
                    onClick={() =>
                      browserHistory.push(
                        chapterBlockPath(
                          course.id,
                          selectedChapter.id,
                          block.id,
                          { isPreview: isPreviewMode }
                        )
                      )
                    }
                  >
                    Open
                  </button>
                ) : null}
              </Card>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}

ChapterOverviewPage.propTypes = propTypes
ChapterOverviewPage.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    currentCourse: globalSelectors.currentCourse(state),
  }
}

const mapDispatchToProps = {
  fetchLearningHubCourse: apiActions.fetchLearningHubCourse,
  fetchLearningHubCoursePreview: apiActions.fetchLearningHubCoursePreview,
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  ChapterOverviewPage
)
